/**
 * Small labels.
 *
 * Twenty-two badge and pill components in the app collapse to these seven
 * plus Status and Chip, which already existed. The saving is not the file
 * count. It is that the app currently spends eight hues on identity — which
 * plan, which bot, which platform — so by the time colour is used for the
 * one thing it should mean, nobody reads it as meaning anything.
 *
 * Colour means status. Identity is drawn in ink.
 */
import { type ReactNode } from 'react'
import { Bookmark, Clock, FileCheck, Lock, PenLine, Upload } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Fig } from './primitives'
import './marks.css'

/* ---------------- A name for a thing ---------------- */

/**
 * Plan tier, bot, platform, category, role. Never a state.
 *
 * Replaces PlanBadge, which drew five tiers in five hues (blue Essential,
 * purple Growth, amber Portfolio, emerald Enterprise, grey Free), plus
 * BotBadge's six categories and IntegrationBadge's three platforms. None of
 * those colours meant anything a reader could learn, because a tier is not a
 * severity and the order of the hues is not the order of the tiers.
 */
export function Tag({
  children,
  mark,
  logo,
  now,
  className,
}: {
  children: ReactNode
  /** One or two letters, when there is no logo. */
  mark?: string
  logo?: string
  /** The one that is currently selected or in force. */
  now?: boolean
  className?: string
}) {
  return (
    <span className={cn('ds-tag', now && 'ds-tag--now', className)}>
      {(logo || mark) && (
        <span className="ds-tag__mark" aria-hidden="true">
          {logo ? <img src={logo} alt="" /> : mark?.slice(0, 2).toUpperCase()}
        </span>
      )}
      <span>{children}</span>
    </span>
  )
}

/* ---------------- How full something is ---------------- */

/**
 * Ink while there is room; the attention colour only once it is nearly gone.
 *
 * CapacityBadge coloured a dot at every level, so the colour was decoration
 * until the reader worked out the scale. Here nothing is coloured until
 * somebody has to act, which is the only thing colour is for.
 */
export function Capacity({
  used,
  of,
  what,
  tightAt = 0.9,
}: {
  used: number
  of: number
  what: string
  /** The share at which this stops being information and starts being a job. */
  tightAt?: number
}) {
  const full = used >= of
  const tight = of > 0 && used / of >= tightAt
  return (
    <span className={cn('ds-capacity', tight && 'ds-capacity--tight')}>
      {full ? (
        <>
          <b>
            <Fig>{of}</Fig>
          </b>{' '}
          {what}, full
        </>
      ) : (
        <>
          <b>
            <Fig>{used}</Fig>
          </b>{' '}
          of{' '}
          <b>
            <Fig>{of}</Fig>
          </b>{' '}
          {what}
        </>
      )}
    </span>
  )
}

/* ---------------- How long a job takes ---------------- */

const EFFORT_SAID = {
  quick: 'About 5 minutes',
  hour: 'About an hour',
  bigger: 'A bigger piece of work',
} as const

/**
 * The time, and nothing else.
 *
 * EffortBadge printed a level in one of three colours with the time in
 * brackets after it: "Low (5 min)" in yellow, "Medium" in blue, "High" in
 * purple. The level is our word for the time. The time is the thing somebody
 * is deciding on, so it is the thing on screen.
 */
export function Effort({ level }: { level: keyof typeof EFFORT_SAID }) {
  return (
    <span className="ds-effort">
      <Clock aria-hidden="true" />
      {EFFORT_SAID[level]}
    </span>
  )
}

/* ---------------- Where a thing came from ---------------- */

const ORIGIN = {
  ours: { said: 'We wrote this', icon: PenLine },
  theirs: { said: 'Already on your page', icon: FileCheck },
  waiting: { said: 'Written, not live yet', icon: Upload },
} as const

/**
 * Whether we wrote it, whether it was already there, or whether it is
 * written and waiting to go live.
 *
 * This one earns its place: we are answerable for what we wrote and not for
 * what was on the page when we arrived, and a reader cannot tell those apart
 * from the content. SchemaOriginBadge said the same thing with a sparkle for
 * ours and a code bracket for theirs, which reads as "premium" and "basic"
 * rather than "mine" and "yours".
 */
export function Origin({ from }: { from: keyof typeof ORIGIN }) {
  const { said, icon: Icon } = ORIGIN[from]
  return (
    <span className={cn('ds-origin', from === 'waiting' && 'ds-origin--waiting')}>
      <Icon aria-hidden="true" />
      {said}
    </span>
  )
}

/* ---------------- Something this plan does not include ---------------- */

/** Names the plan that includes it, because "upgrade" is not an answer. */
export function NeedsPlan({ plan }: { plan: string }) {
  return (
    <span className="ds-needsplan">
      <Lock aria-hidden="true" />
      On {plan} and up
    </span>
  )
}

/* ---------------- A price somebody keeps ---------------- */

/**
 * Was purple with a sparkle. Purple is not in this system, and the sparkle
 * said "premium" when the fact is narrower and better: this price is theirs
 * and it is not going up.
 *
 * Deliberately not a padlock. NeedsPlan a few lines up is a padlock and means
 * a feature somebody cannot have; these two sit on the same billing screen
 * and mean opposite things, so they must not share a mark.
 */
export function Kept({ children }: { children?: ReactNode }) {
  return (
    <span className="ds-kept">
      <Bookmark aria-hidden="true" />
      {children ?? 'Your price, kept'}
    </span>
  )
}

/* ---------------- Newly arrived ---------------- */

/**
 * Ink, not a colour. Colour in this system means one of two things: this
 * needs you, or this is a link. A new feature is neither, so it is drawn the
 * way every other plain fact is drawn. Replaces NewPill, GlowingBadge and
 * FloatingBadge, two of which animated.
 */
export function NewMark({ children, dot }: { children?: ReactNode; dot?: boolean }) {
  if (dot) return <span className="ds-new ds-new--dot" aria-label="New" />
  return <span className="ds-new">{children ?? 'New'}</span>
}
